import { Alert, Box, Button, Fade, Typography, useTheme } from '@mui/material';
import { Form, Formik } from 'formik';
import { FC, useState } from 'react';
import { PASSWORD_CHANGE_ERROR } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { PASSWORD_REGEX } from '@acorn-portal/shared/src/utils/constants/Regex';
import * as Yup from 'yup';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import { UserClient } from '@acorn-portal/shared/src/services/authenticationApi';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import CheckIcon from '@acorn-portal/shared/src/assets/shared/icons/circled-check-green.svg?react';
import { Home } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import GetRealm from '@acorn-portal/shared/src/utils/getRealm';
import PasswordRequirements from '../../copy/PasswordRequirements';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface IProps {
	customerEmail: string;
}

interface IChangePasswordForm {
	newPassword: string;
	newPasswordConfirmation: string;
}

const getApiErrorMessage = (error: unknown): string | undefined => {
	const response = (error as { response?: unknown })?.response;
	if (!response) {
		return undefined;
	}

	if (typeof response === 'object') {
		const errorObj = response as { detail?: string; title?: string };
		return errorObj.detail ?? errorObj.title;
	}

	if (typeof response === 'string') {
		try {
			const parsed = JSON.parse(response) as { detail?: string; title?: string };
			return parsed.detail ?? parsed.title;
		} catch {
			return undefined;
		}
	}

	return undefined;
};

const initialValues: IChangePasswordForm = {
	newPassword: '',
	newPasswordConfirmation: '',
};

const validationSchema = Yup.object().shape({
	newPassword: Yup.string()
		.required(PASSWORD_CHANGE_ERROR.newPassword)
		.matches(PASSWORD_REGEX, PASSWORD_CHANGE_ERROR.passwordComplexity),
	newPasswordConfirmation: Yup.string()
		.required(PASSWORD_CHANGE_ERROR.newPasswordConfirmation)
		.test('password-confirmation', PASSWORD_CHANGE_ERROR.passwordMismatch, (value, form) => {
			if (form.parent) {
				return value === form.parent?.newPassword;
			}
			return false;
		}),
});

const ChangePasswordForm: FC<IProps> = ({ customerEmail }) => {
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const themeUtils = useTheme();
	const navigate = useNavigate();

	const handleOnSubmit = async (form: IChangePasswordForm) => {
		try {
			setLoading(true);
			const client = new UserClient(undefined, API);
			await client.setCustomerPassword({
				newPassword: form.newPassword,
				email: customerEmail,
				realm: GetRealm(),
			});
			setLoading(false);
			setSuccess(true);
		} catch (error) {
			console.error(error);
			setErrorMessage(getApiErrorMessage(error) ?? 'Unable to reset your password. Please try again.');
			setLoading(false);
		}
	};

	return (
		<Box sx={{ paddingBottom: 2 }}>
			<LoadingModal open={loading} />
			{success ? (
				<Fade in timeout={600}>
					<Box sx={{ mt: 2 }}>
						<Typography variant='h5' color='secondary'>
							Password Changed!
						</Typography>
						<Typography variant='body2'>
							Your password has been changed. Remember to login with your new password next time.
						</Typography>
						<Box sx={{ margin: 2 }}>
							<CheckIcon color={`${themeUtils.palette.success.main} !important`} width='100px' />
						</Box>

						<Button
							variant='contained'
							fullWidth
							sx={{ maxWidth: '300px' }}
							startIcon={<Home />}
							onClick={() => navigate('/portal/home')}
						>
							Home
						</Button>
					</Box>
				</Fade>
			) : (
				<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleOnSubmit}>
					{(formik) => (
						<Form>
							<FormikLogErrors />
							<PasswordRequirements password={formik.values.newPassword} />
							<FormTextField
								id='newPassword'
								name='newPassword'
								label='New Password'
								format='password'
								error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
								helperText={formik.touched.newPassword && formik.errors.newPassword}
								onChange={formik.handleChange}
								value={formik.values.newPassword}
							/>
							<FormTextField
								id='newPasswordConfirmation'
								name='newPasswordConfirmation'
								label='Password Confirmation'
								format='password'
								error={formik.touched.newPasswordConfirmation && Boolean(formik.errors.newPasswordConfirmation)}
								helperText={formik.touched.newPasswordConfirmation && formik.errors.newPasswordConfirmation}
								onChange={formik.handleChange}
								value={formik.values.newPasswordConfirmation}
							/>
							<Button variant='contained' fullWidth sx={{ maxWidth: '300px' }} type='submit'>
								Change Password
							</Button>
							{errorMessage && (
								<Alert severity='warning' sx={{ mt: 2 }}>
									{errorMessage}
								</Alert>
							)}
						</Form>
					)}
				</Formik>
			)}
		</Box>
	);
};

export default ChangePasswordForm;
